import { OverflowDirection } from "@redotech/react-util/overflow";
import CopyIcon from "@redotech/redo-icons/copy-01.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { OverflowTooltip } from "@redotech/redo-web/arbiter-components/tooltip/overflow-tooltip";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, type MouseEvent, useState } from "react";
import * as styles from "./tracking-numbers-display.module.css";

export interface TrackingNumberDisplayEntry {
  link: string;
  trackingNumber: string;
}

export interface TrackingNumbersDisplayProps {
  onActionClick?(event: MouseEvent): void;
  tracking: TrackingNumberDisplayEntry[];
}

export const TrackingNumbersDisplay = memo(function TrackingNumbersDisplay({
  onActionClick,
  tracking,
}: TrackingNumbersDisplayProps) {
  if (!tracking.length) {
    return null;
  }

  const stopRowNavigation = (event: MouseEvent) => {
    event.stopPropagation();
    onActionClick?.(event);
  };

  return (
    <Flex align="center" gap="sm" minW="none" onClick={stopRowNavigation}>
      <Flex gap="sm" minW="none" overflow="hidden">
        {tracking.map(({ trackingNumber, link }) => (
          <TrackingNumberLabel
            displayText={`#${trackingNumber}`}
            key={trackingNumber}
            link={link}
            onNavigate={stopRowNavigation}
          />
        ))}
      </Flex>
      <RedoButton
        aria-label="Copy"
        IconLeading={CopyIcon}
        onClick={async (event) => {
          stopRowNavigation(event);
          await navigator.clipboard.writeText(
            tracking.map((entry) => entry.trackingNumber).join(", "),
          );
        }}
        size="xs"
      />
    </Flex>
  );
});

const TrackingNumberLabel = memo(function TrackingNumberLabel({
  displayText,
  link,
  onNavigate,
}: {
  displayText: string;
  link: string;
  onNavigate(event: MouseEvent): void;
}) {
  const [overflowRef, setOverflowRef] = useState<HTMLElement | null>(null);

  return (
    <OverflowTooltip
      direction={OverflowDirection.Horizontal}
      overflowRef={overflowRef}
      tooltipProps={{ title: displayText }}
      wrapperClass={styles.label}
    >
      <Text
        className={link ? styles.link : undefined}
        fontSize="xs"
        minW="none"
        onClick={
          link
            ? (event) => {
                onNavigate(event);
                window.open(link, "_blank");
              }
            : undefined
        }
        overflow="hidden"
        ref={setOverflowRef}
        textColor="secondary"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {displayText}
      </Text>
    </OverflowTooltip>
  );
});
